import React from "react";
import {
  Main as PageLayoutMain,
  Root as PageLayoutRoot,
  TopNav,
  TopNavEnd,
  TopNavStart,
} from "@atlassian/navigation-system";
import { parseHex } from "@atlaskit/navigation-system/experimental/color-utils/parse-hex";
import { type UserAccount } from "../../clients/gateway-client/types";
import { type Site } from "../../hooks/useSite";
import { useCompanyThemingNavbarColor } from "../../hooks/company-theming/useCompanyThemingColors";
import { Profile } from "../profile/Profile";
import { ExtensionTopBarCompanyLogo } from "./ExtensionTopBarCompanyLogo";

export interface ProfileOnlyTopBarLayoutProps {
  userAccount: UserAccount;
  site?: Site;
  children: React.ReactNode;
  testId?: string;
}

/**
 * A lightweight top bar layout that only shows the company logo and profile.
 * Used in contexts where we don't need the full navigation features.
 */
const ProfileOnlyTopBarLayout = ({
  userAccount,
  site,
  children,
  testId = "profile-only-top-navigation",
}: ProfileOnlyTopBarLayoutProps): React.JSX.Element => {
  const customNavbarColor = useCompanyThemingNavbarColor();

  return (
    <PageLayoutRoot>
      <TopNav
        testId={testId}
        UNSAFE_theme={{
          backgroundColor: parseHex(customNavbarColor),
          highlightColor: parseHex(customNavbarColor),
        }}
      >
        <TopNavStart>
          <ExtensionTopBarCompanyLogo site={site} userAccount={userAccount} />
        </TopNavStart>
        <TopNavEnd>
          <Profile showWhatsNewButton={false} site={site} userAccount={userAccount} />
        </TopNavEnd>
      </TopNav>
      <PageLayoutMain>{children}</PageLayoutMain>
    </PageLayoutRoot>
  );
};

export default ProfileOnlyTopBarLayout;
